import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './CheckoutFailed.css';

interface CheckoutFailedProps {}

const CheckoutFailed: React.FC<CheckoutFailedProps> = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleRetryPurchase = () => {
    // Get event ID from session storage or URL params if available
    const eventId = sessionStorage.getItem('currentEventId');
    if (eventId) {
      navigate(`/events/${eventId}`);
    } else {
      navigate('/events');
    }
  };

  const handleBackToEvents = () => {
    navigate('/events');
  };

  return (
    <div className="checkout-failed">
      <div className="failed-container">
        {/* Failed Header */}
        <div className="failed-header">
          <div className="failed-icon">❌</div>
          <h1>{t('checkout.paymentFailed')}</h1>
          <p className="failed-message">{t('checkout.paymentFailedMessage')}</p>
        </div>

        {/* Error Details */}
        <div className="error-details">
          <div className="error-card">
            <h3>{t('checkout.whatHappened')}</h3>
            <p>{t('checkout.paymentNotProcessed')}</p>
            
            {sessionId && (
              <div className="session-info">
                <small>{t('checkout.sessionId')}: {sessionId}</small>
              </div>
            )}
          </div>
        </div>

        {/* Common Issues */}
        <div className="common-issues">
          <h3>{t('checkout.commonIssues')}</h3>
          <div className="issues-list">
            <div className="issue-item">
              <span className="issue-icon">💳</span>
              <div className="issue-content">
                <h4>{t('checkout.cardDeclined')}</h4>
                <p>{t('checkout.cardDeclinedDescription')}</p>
              </div>
            </div>
            <div className="issue-item">
              <span className="issue-icon">🏦</span>
              <div className="issue-content">
                <h4>{t('checkout.insufficientFunds')}</h4>
                <p>{t('checkout.insufficientFundsDescription')}</p>
              </div>
            </div>
            <div className="issue-item">
              <span className="issue-icon">🌐</span>
              <div className="issue-content">
                <h4>{t('checkout.connectionIssue')}</h4>
                <p>{t('checkout.connectionIssueDescription')}</p>
              </div>
            </div>
            <div className="issue-item">
              <span className="issue-icon">⏰</span>
              <div className="issue-content">
                <h4>{t('checkout.sessionExpired')}</h4>
                <p>{t('checkout.sessionExpiredDescription')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Next Steps */}
        <div className="next-steps">
          <h3>{t('checkout.nextSteps')}</h3>
          <div className="steps-list">
            <div className="step-item">
              <span className="step-number">1</span>
              <div className="step-content">
                <p>{t('checkout.checkPaymentMethod')}</p>
              </div>
            </div>
            <div className="step-item">
              <span className="step-number">2</span>
              <div className="step-content">
                <p>{t('checkout.ensureConnection')}</p>
              </div>
            </div>
            <div className="step-item">
              <span className="step-number">3</span>
              <div className="step-content">
                <p>{t('checkout.tryAgain')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Support Information */}
        <div className="support-info">
          <div className="support-card">
            <h4>{t('checkout.needHelp')}</h4>
            <p>{t('checkout.supportDescription')}</p>
            <div className="support-contacts">
              <div className="contact-item">
                <span className="contact-icon">📧</span>
                <span>support@example.com</span>
              </div>
              <div className="contact-item">
                <span className="contact-icon">📞</span>
                <span>+351 XXX XXX XXX</span>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="failed-actions">
          <button onClick={handleRetryPurchase} className="btn-primary">
            {t('checkout.retryPurchase')}
          </button>
          <button onClick={handleBackToEvents} className="btn-secondary">
            {t('checkout.backToEvents')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutFailed;